import React from "react";
import { PageHeader, Section } from "@/components/Common";
import { Target, Eye, Heart, Award, Compass } from "lucide-react";

const ABOUT_IMG = "https://images.unsplash.com/photo-1509099863731-ef4bff19e808?crop=entropy&cs=srgb&fm=jpg&q=85&w=1200";

export default function About() {
  return (
    <div data-testid="about-page">
      <PageHeader eyebrow="About BCOL" title="A community-led organization preserving heritage and unlocking opportunity."
        subtitle="Born in Ohio, rooted in Guinea — BCOL serves families, students, and elders across the African diaspora."
        image={ABOUT_IMG} />

      <Section>
        <div className="grid lg:grid-cols-3 gap-7">
          {[
            { i: Target, t: "Mission", d: "BCOL is dedicated to strengthening communities, preserving cultural heritage, supporting educational achievement, and empowering individuals and families within the African diaspora through service, leadership, and community engagement." },
            { i: Eye, t: "Vision", d: "A thriving, united, and empowered community where culture is celebrated, excellence is recognized, and future generations are inspired to lead and succeed." },
            { i: Compass, t: "Values", d: "Service. Integrity. Heritage. Excellence. Inclusion. These five values guide every program, partnership, and decision we make." },
          ].map(({ i: Icon, t, d }) => (
            <div key={t} className="card-soft p-8" data-testid={`about-${t.toLowerCase()}`}>
              <div className="w-12 h-12 rounded-xl bg-[var(--bcol-navy)] text-[var(--bcol-gold)] flex items-center justify-center mb-5"><Icon className="w-6 h-6" /></div>
              <h3 className="font-display text-2xl text-[var(--bcol-navy)] mb-3">{t}</h3>
              <p className="text-[var(--bcol-muted)] leading-relaxed text-sm">{d}</p>
            </div>
          ))}
        </div>
      </Section>

      <Section className="bg-white">
        <div className="grid lg:grid-cols-12 gap-12">
          <div className="lg:col-span-5">
            <div className="eyebrow mb-3">Our Story</div>
            <h2 className="font-display text-4xl text-[var(--bcol-navy)] leading-tight">From kitchen tables to community institution.</h2>
          </div>
          <div className="lg:col-span-7 text-[var(--bcol-muted)] leading-relaxed space-y-4">
            <p>BCOL began as informal gatherings of Guinean families in Columbus, Ohio — celebrations, language lessons, and a place for newcomers to find belonging. As the community grew, so did the need for something more structured.</p>
            <p>In 2019, community leaders formalized BCOL as a nonprofit. Today we host an annual cultural gala, recognize graduating students, distribute scholarships, run community resource drives, and partner with schools and local agencies serving the diaspora.</p>
            <p>Our work is led by a volunteer board of educators, healthcare professionals, business owners, and youth representatives — a true reflection of the community we serve.</p>
          </div>
        </div>
      </Section>

      <Section className="bg-[var(--bcol-bg)] kente-bg">
        <div className="mb-12">
          <div className="eyebrow mb-3">Leadership</div>
          <h2 className="font-display text-4xl text-[var(--bcol-navy)]">Our Board of Directors</h2>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
          {[
            { name: "Moustapha Bayo", role: "President", photo: "https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/xa5svf11_WhatsApp%20Image%202025-09-16%20at%2014.23.31.jpeg" },
            { name: "Bountouraby Sylla", role: "Vice President", photo: "https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/3hw6jgnp_WhatsApp%20Image%202026-06-08%20at%2021.16.30.jpeg" },
            { name: "Adama Bangoura", role: "Treasurer", photo: "https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/i6lfyc1n_WhatsApp%20Image%202026-06-08%20at%2022.59.22.jpeg" },
            { name: "Diene Diallo", role: "Communication & Marketing Secretary", photo: "https://customer-assets.emergentagent.com/job_community-voice-71/artifacts/ur8v2r02_WhatsApp%20Image%202026-06-08%20at%2020.31.37.jpeg" },
          ].map((m) => (
            <div key={m.name} className="card-soft p-6 text-center">
              {m.photo ? (
                <img src={m.photo} alt={m.name}
                  className="w-24 h-24 mx-auto rounded-full object-cover mb-4 ring-2 ring-[var(--bcol-gold)] ring-offset-2" />
              ) : (
                <div className="w-20 h-20 mx-auto rounded-full bg-gradient-to-br from-[var(--bcol-navy)] to-[var(--bcol-gold)] mb-4 flex items-center justify-center font-display text-white text-2xl">{m.name.split(" ").map(s => s[0]).slice(0,2).join("")}</div>
              )}
              <div className="font-display text-lg text-[var(--bcol-navy)]">{m.name}</div>
              <div className="text-xs uppercase tracking-widest text-[var(--bcol-muted)] mt-1">{m.role}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}
